import React, {
  createContext,
  useContext,
  useCallback,
  FC,
  useReducer,
  useState,
} from 'react'

import { startOfMonth } from 'date-fns'
import { useAuth } from 'hooks/auth'
import { OfficeData, SellersData } from 'hooks/salesCashFlow/props'
import { api } from 'services/api'
import {
  paginationReducer,
  PaginationActions,
  PaginationRouteParams,
} from 'services/pagination'
import {
  initialCategory,
  initialSeller,
  initialStore,
} from 'utils/initialStateForSelects'
import { LeavesParams } from 'utils/paramsForRequest'
import { rolePermission } from 'utils/permissions'

import {
  ChipsLeavesCashFlow,
  ChipsLeavesContextData,
  ChipsLeavesProps,
  ChipsStatusParams,
} from './props'
import { ListChipsLeaveResponse } from './props'
import { initialState } from './reducer'
const initialValue = {} as ChipsLeavesContextData
export const ChipsLeavesCashFlowContext = createContext(initialValue)

export const ChipsLeavesCashFlowProvider: FC = props => {
  const { children } = props
  const { user } = useAuth()
  const [store, setStore] = useState<OfficeData>(initialStore)
  const [seller, setSeller] = useState<SellersData>(initialSeller)
  const [startDate, setStartDate] = useState<Date>(startOfMonth(new Date()))
  const [endDate, setEndDate] = useState<Date>(new Date())
  const [category, setCategory] = useState<ChipsLeavesProps>(initialCategory)
  const [state, dispatch] = useReducer(
    paginationReducer<ChipsLeavesCashFlow>(),
    initialState,
  )
  const permission = rolePermission(user?.role.name)
  const getChipsLeaves = useCallback(async () => {
    try {
      dispatch({ type: PaginationActions.Request })
      const paramsFiltred = LeavesParams({
        type: 'leaves_change',
        params: {
          category_eq: category?.labelName,
          seller_user_id_eq: seller?.user_id,
          office_id_eq: store?.id,
          seller_role_name_eq: seller?.role_name,
        },
      })

      const params: PaginationRouteParams & ChipsStatusParams = {
        page: state.current_page,
        per_page: state.per_page,
        date_gt: startDate,
        date_lt: endDate,
        ...paramsFiltred,
      }

      if (permission == 'sellerPermission') {
        const response = await api.get<ListChipsLeaveResponse>(
          `/chips/chips-output/reports-of-seller`,
          { params },
        )
        dispatch({
          type: PaginationActions.RequestSuccess,
          payload: response,
        })
      } else if (permission == 'midlePermission') {
        params.office_id_eq = undefined
        const response = await api.get<ListChipsLeaveResponse>(
          `chips/chips-output/reports-of-office/`,
          { params },
        )
        dispatch({
          type: PaginationActions.RequestSuccess,
          payload: response,
        })
      } else {
        const response = await api.get<ListChipsLeaveResponse>(
          `chips/chips-output/reports`,
          {
            params,
          },
        )
        dispatch({
          type: PaginationActions.RequestSuccess,
          payload: response,
        })
      }
    } catch (e) {
      dispatch({ type: PaginationActions.RequestError })
    }
  }, [state, seller, store, startDate, endDate, category, permission])

  const nextPage = useCallback(() => {
    if (!state.next_page) return
    dispatch({ type: PaginationActions.NextPage })
  }, [state])

  const previousPage = useCallback(() => {
    if (!state.prev_page) return
    dispatch({ type: PaginationActions.PreviousPage })
  }, [state])

  const setLimit = useCallback((limit: number) => {
    dispatch({ type: PaginationActions.SetLimit, payload: limit })
  }, [])

  const setPage = useCallback((page: number) => {
    dispatch({ type: PaginationActions.SetPage, payload: page })
  }, [])

  return (
    <ChipsLeavesCashFlowContext.Provider
      value={{
        ...state,
        startDate,
        endDate,
        category,
        store,
        seller,
        setCategory,
        setStartDate,
        setEndDate,
        setSeller,
        setStore,
        getChipsLeaves,
        nextPage,
        previousPage,
        setLimit,
        setPage,
      }}
    >
      {children}
    </ChipsLeavesCashFlowContext.Provider>
  )
}

export function useChipsLeavesCashFlow(): ChipsLeavesContextData {
  const context = useContext(ChipsLeavesCashFlowContext)

  if (!context || context === initialValue) {
    throw new Error(
      'useChipsCashFlow must be used within a ChipsLeavesCashFlowProvider',
    )
  }

  return context
}
